import { useLocation } from 'react-router-dom';
import { CommonPageProps } from 'src/components/common/CommonPageProps';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { associateGlossaryToResource, deleteGlossary, listGlossaries } from 'src/api/catalog';
import { METADATA_FORM_PREFIX, TABLE_CONTENT_TYPE } from 'src/commons/constants';
import { createGlossaryViewLink, Page } from 'src/routes';
import {
  Box,
  Button,
  CollectionPreferences,
  CollectionPreferencesProps,
  Flashbar,
  Modal,
  Pagination,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  Table,
  TableProps,
  TextContent,
  TextFilter,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { defaultWrapLinesPreference, notificationsPageSizePreference, paginationLabels } from 'src/commons/tables';
import BusinessGlossaryAssociationsTable from 'src/components/metadata/businessGlossaries/businessGlossaryAssociations';
import { isHCAdmin } from 'src/commons/common';
import { ResourceSelectorModal } from 'src/components/common/resourceSelector/resourceSelectorModal';
import { generateArnFromId } from 'src/components/utils/arnUtil';
import { EmptyState } from 'src/commons/EmptyState';
import { PageHeader } from 'src/components/common/PageHeader';
import {
  GLOBAL_GLOSSARY_TYPE,
  glossaryTypeDisplayNameLowerCase,
  GOVERNANCE_TAG_TYPE,
  USER_GLOSSARY_TYPE,
} from 'src/components/metadata/constants';
import { AssociateGlossaryToResourceResponse, GlossaryDetails } from 'aws-sdk/clients/awsdlhybridcatalogservicelambda';

export interface MetadataBusinessGlossariesProps extends CommonPageProps {
  glossarySubType?: string;
}

export const MetadataBusinessGlossaries = (props: MetadataBusinessGlossariesProps) => {
  const { state } = useLocation();
  const [loadingGlossaries, setLoadingGlossaries] = useState(false);
  const [ownerId, setOwnerId] = useState(undefined);
  const [businessGlossaries, setBusinessGlossaries] = useState([]);
  const [parentChildMap, setParentChildMap] = useState(new Map());
  const [selectedGlossary, setSelectedGlossary] = useState(undefined);
  const [notifications, setNotifications] = useState([]);
  const [deleteGlossaryModalVisible, setDeleteGlossaryModalVisible] = useState(false);
  const [associateGlossaryModalVisible, setAssociateGlossaryModalVisible] = useState(false);
  const [associateOrDissociateInProgress, setAssociateOrDissociateInProgress] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [glossarySubType, setGlossarySubType] = useState(undefined);
  const [glossarySubTypeName, setGlossarySubTypeName] = useState('');
  const [isGovernanceTag, setIsGovernanceTag] = useState(false);
  const [shouldRefreshAssociations, setShouldRefreshAssociations] = useState(false);
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 10,
  });

  const { items, collectionProps, paginationProps, filterProps, filteredItemsCount } = useCollection(
    businessGlossaries,
    {
      filtering: {
        noMatch: '',
        empty: <EmptyState title={'No glossaries'} subtitle={'No business glossaries were found.'} />,
      },
      pagination: { pageSize: preferences.pageSize },
      sorting: {},
      selection: {},
      propertyFiltering: {
        filteringProperties: [],
      },
      expandableRows: !isGovernanceTag
        ? undefined
        : {
            getId: (item) => item?.GlossaryId,
            getParentId: (item) => item?.ParentGlossaryId,
          },
    },
  );

  const getAllChildGovernanceTags = (item: GlossaryDetails): GlossaryDetails[] => {
    return parentChildMap && parentChildMap.has(item.GlossaryId) ? parentChildMap.get(item.GlossaryId) : [];
  };

  const expandableRows = collectionProps.expandableRows
    ? {
        ...collectionProps.expandableRows,
        getItemChildren: getAllChildGovernanceTags,
        isItemExpandable: (gl) => parentChildMap && parentChildMap.has(gl.GlossaryId),
      }
    : undefined;

  const getColumnDefinitions = () => {
    const glossaryColumnDefinitions: TableProps.ColumnDefinition<any>[] = [
      {
        id: 'glossaryName',
        header: 'Name',
        cell: (item) => <Link href={createGlossaryViewLink(item.GlossaryId, item.OwnerId)}>{item.Name.split()}</Link>,
        minWidth: 200,
        sortingField: 'GlossaryName',
      },
      {
        id: 'glossaryValue',
        header: isGovernanceTag ? 'Governance tag name' : 'Value',
        cell: (item) => {
          return isGovernanceTag ? (
            <Link href={`${Page.GOVERNANCE_TAGS}/${item.GlossaryId}/${item.OwnerId}`}>{item.GlossaryValue}</Link>
          ) : (
            item.GlossaryValue
          );
        },
        minWidth: isGovernanceTag ? 250 : 200,
        sortingField: 'GlossaryValue',
        isRowHeader: true,
      },
      {
        id: 'description',
        header: 'Description',
        cell: (item) => item.Description,
        minWidth: isGovernanceTag ? 350 : 200,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (item) => (
          <StatusIndicator type={item.Status == 'Enabled' ? 'success' : 'stopped'}>{item.Status}</StatusIndicator>
        ),
        minWidth: isGovernanceTag ? 50 : 100,
      },
      {
        id: 'type',
        header: 'Type',
        cell: (item) => item.GlossaryType.replace('Glossary#', ''),
        minWidth: isGovernanceTag ? 50 : 100,
      },
      {
        id: 'visibility',
        header: 'Visibility',
        cell: (item) => item.Visibility,
        minWidth: isGovernanceTag ? 50 : 100,
      },
    ];
    if (isGovernanceTag) {
      // remove the Name of the business glossary - Fixed for governance tags
      glossaryColumnDefinitions.splice(0, 1);
    }
    return glossaryColumnDefinitions;
  };

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    const activeGroup = props.activeGroup;
    const activeWorkspace = props.activeWorkspace;
    const ownerId = activeWorkspace ? activeWorkspace.workspaceId : activeGroup;
    setOwnerId(ownerId);
    if (props.glossarySubType) {
      setGlossarySubType(props.glossarySubType);
      setGlossarySubTypeName(glossaryTypeDisplayNameLowerCase.get(props.glossarySubType));
      setIsGovernanceTag(GOVERNANCE_TAG_TYPE === props.glossarySubType);
    }
    if (!!state && !!state.status && !!state.message) {
      setNotification(state.status, state.message);
    }
  }, []);

  useEffect(() => {
    if (ownerId) {
      handleRefresh();
    }
  }, [ownerId]);

  const handleRefresh = async () => {
    setLoadingGlossaries(true);
    setSelectedGlossary(undefined);
    await getGlossariesForOwner();
  };

  const getGlossariesForOwner = async () => {
    setLoadingGlossaries(true);
    try {
      let listGlossariesResult = await listGlossaries({
        OwnerId: ownerId,
        GlossarySubType: glossarySubType,
      });
      let glossaries = listGlossariesResult.GlossaryDetailsList;
      while (!!listGlossariesResult.NextToken) {
        listGlossariesResult = await listGlossaries({
          OwnerId: ownerId,
          NextToken: listGlossariesResult.NextToken,
          GlossarySubType: glossarySubType,
        });
        glossaries.push(...listGlossariesResult.GlossaryDetailsList);
      }
      if (isGovernanceTag) {
        const globalGovernanceTags = glossaries.filter(
          (glossary) =>
            GOVERNANCE_TAG_TYPE === glossary.GlossarySubType && GLOBAL_GLOSSARY_TYPE === glossary.GlossaryType,
        );
        const userGovernanceTags = glossaries.filter(
          (glossary) =>
            GOVERNANCE_TAG_TYPE === glossary.GlossarySubType && USER_GLOSSARY_TYPE === glossary.GlossaryType,
        );
        const parentChildGovernanceTagMap = new Map();
        userGovernanceTags.forEach((governanceTag) => {
          const childList = parentChildGovernanceTagMap.get(governanceTag.ParentGlossaryId);
          if (!childList) {
            parentChildGovernanceTagMap.set(governanceTag.ParentGlossaryId, [governanceTag]);
          } else {
            childList.push(governanceTag);
          }
        });
        setParentChildMap(parentChildGovernanceTagMap);
        // if governance tag, only include global glossaries here
        setBusinessGlossaries(globalGovernanceTags);
      } else {
        // set all glossaries
        setBusinessGlossaries(glossaries);
      }
    } catch (err) {
      setNotification('error', `Unable to load glossaries. ${err.message}`);
    } finally {
      setLoadingGlossaries(false);
    }
  };

  const setNotification = async (header, message) => {
    if (header === 'error') {
      setNotifications([
        {
          type: 'error',
          content: message,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    } else {
      setNotifications([
        {
          type: 'success',
          content: message,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
  };

  const shouldEnableAdminPermissions = () => {
    if (!selectedGlossary) return false;
    if (props.activeGroup == selectedGlossary.OwnerId || props.activeWorkspace?.workspaceId == selectedGlossary.OwnerId)
      return true;
    if (isHCAdmin(props.activeGroup)) return true;
    return false;
  };

  const getFilterCounterText = (count) => `${count} ${count === 1 ? 'match' : 'matches'}`;

  const businessGlossaryButtonOptions = () => {
    return [
      {
        text: '',
        icon: 'refresh',
        onItemClick: handleRefresh,
      },
      {
        text: 'Actions',
        onItemClick: handleBusinessGlossaryButtonAction,
        items: [
          {
            text: 'Edit',
            id: 'editGlossary',
            disabled: !shouldEnableAdminPermissions(),
          },
          {
            text: 'Delete',
            id: 'deleteGlossary',
            disabled: !shouldEnableAdminPermissions(),
          },
          {
            text: 'Associate',
            id: 'associateGlossary',
            disabled: !selectedGlossary || (isGovernanceTag && selectedGlossary?.GlossaryType === GLOBAL_GLOSSARY_TYPE),
            disabledReason:
              isGovernanceTag && selectedGlossary?.GlossaryType === GLOBAL_GLOSSARY_TYPE
                ? 'You cannot associate top-level governance tags to resources. Please select a sub governance tag to associate.'
                : undefined,
          },
        ],
      },
      {
        text: `Create ${glossarySubTypeName}`,
        variant: 'primary',
        onItemClick: () =>
          props.setRedirect(!isGovernanceTag ? Page.CREATE_BUSINESS_GLOSSARY : Page.CREATE_GOVERNANCE_TAG, false, {
            ownerId: ownerId,
            glossarySubType: glossarySubType,
          }),
      },
    ];
  };

  const handleBusinessGlossaryButtonAction = async (e) => {
    if (e.detail.id === 'editGlossary') {
      handleEditBusinessGlossary();
    } else if (e.detail.id === 'deleteGlossary') {
      setDeleteGlossaryModalVisible(true);
    } else if (e.detail.id == 'associateGlossary') {
      setAssociateGlossaryModalVisible(true);
    }
  };

  const handleEditBusinessGlossary = async () => {
    if (selectedGlossary !== undefined) {
      props.setRedirect(!isGovernanceTag ? Page.EDIT_BUSINESS_GLOSSARY : Page.EDIT_GOVERNANCE_TAG, false, {
        glossaryId: selectedGlossary.GlossaryId,
        glossaryOwnerId: selectedGlossary.OwnerId,
        glossarySubType: glossarySubType,
      });
    } else {
      setNotification('error', 'Please select a form to edit');
    }
  };

  const handleDeleteBusinessGlossary = async () => {
    setButtonLoading(true);
    try {
      let deleteGlossaryResult = await deleteGlossary({
        GlossaryId: selectedGlossary.GlossaryId,
        OwnerId: selectedGlossary.OwnerId,
      });
      if (deleteGlossaryResult.Message == 'Success') {
        await setNotification('success', 'Success');
      } else {
        await setNotification(
          'error',
          `Failed to delete the ${glossarySubTypeName}. Reason: ${deleteGlossaryResult.Message}`,
        );
      }
    } catch (err) {
      await setNotification('error', `Error in deleting the ${glossarySubTypeName}. ${err.message}`);
    } finally {
      setButtonLoading(false);
      setDeleteGlossaryModalVisible(false);
      await handleRefresh();
    }
  };

  async function invokeAPIToAssociate(resourceIdOrArn: string) {
    let resourceArn: string;
    if (resourceIdOrArn.startsWith('arn:')) {
      // Gladstone's FGAP arn will be used as is.
      resourceArn = resourceIdOrArn;
    } else {
      resourceArn = generateArnFromId(resourceIdOrArn);
    }
    let associateGlossaryRequest;
    if (resourceIdOrArn.startsWith(METADATA_FORM_PREFIX)) {
      const isWorkspace = !!props.activeWorkspace;
      const ownerId = isWorkspace ? props.activeWorkspace.workspaceId : props.activeGroup;
      associateGlossaryRequest = {
        GlossaryId: selectedGlossary?.GlossaryId,
        OwnerId: selectedGlossary?.OwnerId,
        FormOwnerId: ownerId,
        ResourceArn: resourceIdOrArn,
      };
    } else {
      associateGlossaryRequest = {
        GlossaryId: selectedGlossary?.GlossaryId,
        OwnerId: selectedGlossary?.OwnerId,
        ResourceArn: resourceArn,
      };
    }
    return await associateGlossaryToResource(associateGlossaryRequest);
  }

  const handleAssociateGlossary = async (resourceIdOrArn: string) => {
    try {
      setAssociateOrDissociateInProgress(true);
      if (!resourceIdOrArn) {
        setNotification('error', 'Please select a resource to associate!');
        return;
      }
      const associateGlossaryResult = await invokeAPIToAssociate(resourceIdOrArn);
      if (associateGlossaryResult.Message == 'Success') {
        setNotification('success', `Successfully associated to the ${glossarySubTypeName}`);
      } else {
        setNotification('error', `Failure in associating resource :${associateGlossaryResult.Message}`);
      }
    } catch (err) {
      setNotification('error', `Error in associating ${glossarySubTypeName} :${err.message}`);
    } finally {
      setAssociateGlossaryModalVisible(false);
      setAssociateOrDissociateInProgress(false);
      //toggle
      setShouldRefreshAssociations(!shouldRefreshAssociations);
    }
  };

  const handleMultipleAssociateGlossaries = async (resourceIdOrArns: string[]) => {
    try {
      setAssociateOrDissociateInProgress(true);
      if (!resourceIdOrArns || resourceIdOrArns.length == 0) {
        setNotification('error', 'Please select one or more resources to associate!');
        return;
      }
      console.log(`Trying to associate ${resourceIdOrArns} to the ${selectedGlossary?.GlossaryId}`);
      const allAssnResults: AssociateGlossaryToResourceResponse[] = [];
      // run 5 associations concurrently at once
      for (let i = 0; i < resourceIdOrArns.length; i += 5) {
        const batch = resourceIdOrArns.slice(i, i + 5);
        const results = await Promise.all(batch.map(async (arn) => await invokeAPIToAssociate(arn)));
        results.forEach((res) => allAssnResults.push(res));
      }
      const successCount = allAssnResults
        .map((res) => res?.Message?.toLowerCase())
        .filter((msg) => msg == 'success').length;
      if (successCount == allAssnResults.length) {
        setNotification('success', `Successfully associated ${successCount} resource(s) to the ${glossarySubTypeName}`);
      } else {
        setNotification('error', `Failure in associating ${allAssnResults.length - successCount} resource(s)!`);
      }
    } catch (err) {
      setNotification('error', `Error in associating ${glossarySubTypeName} :${err.message}`);
    } finally {
      setAssociateGlossaryModalVisible(false);
      setAssociateOrDissociateInProgress(false);
      // toggle
      setShouldRefreshAssociations(!shouldRefreshAssociations);
    }
  };

  return (
    <>
      <Modal
        visible={deleteGlossaryModalVisible}
        header={[`Confirm deletion`]}
        onDismiss={() => setDeleteGlossaryModalVisible(false)}
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button variant='primary' loading={buttonLoading} onClick={handleDeleteBusinessGlossary}>
                Confirm
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <TextContent>
          Are you sure you want to delete {glossarySubTypeName} <strong>'{selectedGlossary?.Name}'</strong>?
          <br />
          <br />
          <strong>Please delete all the associated resources before deleting the {glossarySubTypeName}.</strong>
        </TextContent>
      </Modal>
      <ResourceSelectorModal
        activeGroup={props.activeGroup}
        activeWorkspace={props.activeWorkspace}
        visible={associateGlossaryModalVisible}
        onSelect={handleAssociateGlossary}
        close={() => setAssociateGlossaryModalVisible(false)}
        disableGroupAndWorkspaceSelection={true}
        disableTagSelection={true}
        disableMetadataFormSelection={isGovernanceTag}
        disableCatalogSelection={isGovernanceTag}
        enableMultiSelectForTableAndFga={isGovernanceTag}
        onMultiSelect={handleMultipleAssociateGlossaries}
      />
      <SpaceBetween size='l'>
        {notifications?.length > 0 && <Flashbar items={notifications} />}
        {associateOrDissociateInProgress && <Spinner size='large' />}
        <Table
          {...collectionProps}
          selectionType='single'
          onSelectionChange={({ detail }) => {
            setSelectedGlossary(detail.selectedItems[0]);
          }}
          selectedItems={[selectedGlossary]}
          loadingText={`Loading ${!isGovernanceTag ? 'glossaries...' : 'governance tags...'}`}
          loading={loadingGlossaries}
          columnDefinitions={getColumnDefinitions()}
          items={items}
          wrapLines={isGovernanceTag}
          expandableRows={expandableRows}
          empty={
            <Box textAlign='center' color='inherit'>
              <b>No {!isGovernanceTag ? 'glossaries' : 'governance tags'}</b>
              <Box variant='p' color='inherit'>
                No {!isGovernanceTag ? 'glossaries' : 'governance tags'} to display. Create one...
              </Box>
            </Box>
          }
          filter={
            <TextFilter
              {...filterProps}
              filteringAriaLabel='Filter resources'
              filteringPlaceholder='Find resources'
              countText={getFilterCounterText(filteredItemsCount)}
            />
          }
          resizableColumns={true}
          header={
            <>
              <PageHeader
                buttons={businessGlossaryButtonOptions()}
                counter={`(${businessGlossaries.length})`}
                header={`${!isGovernanceTag ? 'Business glossaries' : 'Governance tags'}`}
                description={`${
                  !isGovernanceTag
                    ? 'Business glossaries are a centralized collection of standardized business terms and ' +
                      'their definitions used within an organization'
                    : 'Governance tags allow ' +
                      'logical grouping of data assets based on regulatory requirements or domain area.'
                }`}
              />
            </>
          }
          preferences={
            <CollectionPreferences
              title={'Preferences'}
              confirmLabel={'Confirm'}
              cancelLabel={'Cancel'}
              preferences={preferences}
              onConfirm={({ detail }) => setPreferences(detail)}
              pageSizePreference={notificationsPageSizePreference}
              wrapLinesPreference={defaultWrapLinesPreference}
            />
          }
          pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels} />}
        />
        {selectedGlossary && (
          <BusinessGlossaryAssociationsTable
            glossary={selectedGlossary}
            childGovernanceTagGlossaries={getAllChildGovernanceTags(selectedGlossary)}
            enableCSVDownload={isGovernanceTag}
            ownerId={ownerId}
            setNotification={setNotification}
            activeWorkspace={props.activeWorkspace}
            isGovernanceTag={isGovernanceTag}
            refreshAssociations={shouldRefreshAssociations}
          />
        )}
      </SpaceBetween>
    </>
  );
};

export default MetadataBusinessGlossaries;
